import concat from 'lodash/concat'
import findIndex from 'lodash/findIndex'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import slice from 'lodash/slice'
import { useCallback, useState } from 'react'

import { ISearchFormHeaderProps } from '../../templates/house-search-layout/HouseSearchLayout'

export type Inputs = Omit<
  ISearchFormHeaderProps,
  'travelType' | 'houseCanBePrivatised'
>
export type CurrentFieldName = keyof Inputs | 'default'

const reOrderArray = (
  array: Array<keyof Inputs>,
  currentField?: keyof Inputs,
) => {
  if (!currentField) {
    return array
  }

  const index = findIndex(array, (field) => field === currentField)
  return concat(slice(array, index), slice(array, 0, index))
}

export function useFieldNavigator(fields: Array<keyof Inputs>) {
  const [currentField, setCurrentField] = useState<CurrentFieldName>('default')

  const onChangeCurrentField = (field: CurrentFieldName) => {
    setCurrentField(field)
  }

  const isFieldEmpty = (
    formValues: Inputs,
    fieldName: keyof Inputs,
  ): boolean => {
    const value = formValues[fieldName]

    if (fieldName === 'dates') {
      const datesValue = formValues?.dates
      return isNil(datesValue?.startDate) || isNil(datesValue?.endDate)
    }

    if (fieldName === 'adults') {
      const adultsValue = formValues?.adults
      return isNil(adultsValue) || adultsValue === 0
    }

    return isNil(value) || isEmpty(value)
  }

  const openNextField = useCallback(
    (formValues: Inputs, onClose?: () => void, currentField?: keyof Inputs) => {
      const _fields = reOrderArray(fields, currentField)
      const nextField = _fields.find((field) => isFieldEmpty(formValues, field))
      if (nextField) {
        onChangeCurrentField(nextField)
      } else {
        onChangeCurrentField('default')
        onClose?.()
      }
    },
    [fields],
  )

  const formIsValid = (formValues: Inputs): boolean => {
    return fields.every((field) => !isFieldEmpty(formValues, field))
  }

  const onReset = () => {
    onChangeCurrentField('default')
  }

  const isFieldActive = (fieldName: keyof Inputs) => fieldName === currentField

  return {
    currentField,
    isFieldActive,
    openNextField,
    onReset,
    formIsValid,
    onChangeCurrentField,
  }
}
